/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/periodPicker.scss';

import {CloseButton, WithBackground} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';
import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import {ActivateHandle, DashboardPeriod, DashboardPeriods, PeriodKey} from '../../../util/interfaces';
import {BolmateButton, BolmateSelect, DvrdDatePicker} from '../../controls';
import {getDashboardPeriod} from '../../../util/utils';
import {useTranslation} from 'react-i18next';

interface Props {
    onSubmit: (period: DashboardPeriod | null) => void;
    period: DashboardPeriod | null;
}

const selectItems = DashboardPeriods.map((period: DashboardPeriod) => ({
    value: period.key,
    label: period.label
}));

function PeriodPicker(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {onSubmit} = props;
    const [active, setActive] = useState(false);
    const [period, setPeriod] = useState<DashboardPeriod | null>(props.period);

    function activate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
    }

    function _onSubmit() {
        onClose();
        onSubmit(period);
    }

    function onSelect(value: PeriodKey) {
        const period = getDashboardPeriod(value);
        setPeriod(period);
    }

    function onChangePeriodStart(value: IDate) {
        let _period: DashboardPeriod = period ?? {periodEnd: null, periodStart: null, key: PeriodKey.ALL, label: ''};
        _period = {..._period, periodStart: value};
        setPeriod(_period);
    }

    function onChangePeriodEnd(value: IDate) {
        let _period: DashboardPeriod = period ?? {periodEnd: null, periodStart: null, key: PeriodKey.ALL, label: ''};
        _period = {..._period, periodEnd: value};
        setPeriod(_period);
    }

    useImperativeHandle(ref, () => ({activate}));

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='period-picker default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('dashboard.period.title', 'Periode aanpassen')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                <div className='body-container'>
                    <BolmateSelect
                        value={period?.key ?? ''}
                        onChange={onSelect}
                        items={selectItems}
                        label={t('dashboard.period.label', 'Periode')}
                        className='period-select'
                    />
                    <div className='periods'>
                        <DvrdDatePicker
                            onChange={onChangePeriodStart}
                            value={period?.periodStart ?? null}
                            label={t('dashboard.period.from', 'Vanaf')}
                            disabled={period?.key !== PeriodKey.CUSTOM}
                            dateFormat='DD-MM-YYYY'
                            placeholder={t('dashboard.filter.datePlaceholder', 'dd-mm-jjjj')}
                            closeOnChange
                        />
                        <DvrdDatePicker
                            onChange={onChangePeriodEnd}
                            value={period?.periodEnd ?? null}
                            label={t('dashboard.period.to', 'Tot en met')}
                            disabled={period?.key !== PeriodKey.CUSTOM}
                            dateFormat='DD-MM-YYYY'
                            placeholder={t('dashboard.filter.datePlaceholder', 'dd-mm-jjjj')}
                            closeOnChange
                        />
                    </div>
                </div>
                <div className='footer-container'>
                    <BolmateButton onClick={onClose} label={t('common.close', 'Sluiten')} secondary />
                    <BolmateButton onClick={_onSubmit} label={t('common.apply', 'Toepassen')} />
                </div>
            </div>
        </WithBackground>
    );
}

export default forwardRef<ActivateHandle, Props>(PeriodPicker);
